import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IExercise } from '../../../../../core/models/exercise';
import { ICourseSSRExam } from '../../../models/Revision.model';
import { getCourseSSRExam, remakeCourseSSRExam } from '../../../queries/SSR';
import { useCoursesStore } from '../../../stores/courseStore';

export const useRevisionSSRExamFeedbackHook = () => {
  const [exercises, setExercises] = useState<IExercise[]>([]);
  const [feedbackData, setFeedbackData] = useState({
    wrongAnswersTotal: 0,
    correctAnswersTotal: 0,
    correctAnswersPercentage: 0,
    timeSpent: 0,
  });

  const { slug, id } = useParams();

  const { course } = useCoursesStore();

  const navigate = useNavigate();

  const { data, isError, error, isSuccess, isPending } = useQuery({
    queryKey: ['fetch-course-srr-exam-feedback', slug, id],
    queryFn: () => getCourseSSRExam(slug || '', id || ''),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const organizeExercisesFields = (exam: ICourseSSRExam) => {
    const feedback = exam.feedback;
    const answers = feedback.answers;

    const organizedExercises = exam.simulationWeekQuestions.map((exercise) => {
      let isCorrect = false;

      const exerciseAnswerFeedback = answers?.filter(
        (answer) => answer.id === exercise.id
      );

      const exerciseAnswer =
        exerciseAnswerFeedback && exerciseAnswerFeedback.length > 0
          ? exerciseAnswerFeedback[0]
          : null;
      const userAnswers = exerciseAnswer?.userAnswer;

      if (exercise.alternatives) {
        exercise.alternatives = exercise.alternatives.map((alternative) => {
          if (alternative) {
            alternative.id = alternative._id || alternative.id;

            const hasAlternativeSelected = userAnswers?.includes(
              alternative.id || alternative._id
            );

            if (
              hasAlternativeSelected && 
              alternative.correct && 
              exercise.question_type !== 'multiple_answer'
            ) {
              isCorrect = true;
            }

            if (
              !hasAlternativeSelected && 
              alternative.correct && 
              exercise.question_type !== 'multiple_answer'
            ) {
              isCorrect = false;
            }

            alternative.selected = hasAlternativeSelected;
            alternative.showAnswer = true;
            alternative.status =
              hasAlternativeSelected && alternative.correct
                ? 'success'
                : hasAlternativeSelected && !alternative.correct
                  ? 'error'
                  : alternative.correct
                    ? 'correct'
                    : 'default';
          }

          return alternative;
        });

        if(exercise.question_type === 'multiple_answer') {
          const correctAlternatives = exercise.alternatives.filter(alternative => alternative?.correct);
          
          if(correctAlternatives.length === userAnswers?.length && correctAlternatives.every(alternative => userAnswers?.includes(alternative.id))) {
            isCorrect = true;
          }
        }
      }

      exercise.exerciseType = exercise.question_type;
      exercise.done = true;
      exercise.hasAlternativeSelected =
        userAnswers && userAnswers.length > 0 ? true : false;
      exercise.showAnswer = true;
      exercise.correct = isCorrect;

      return exercise;
    });

    return organizedExercises;
  };

  const remakeExam = async () => {
    if (data && course) {
      await remakeCourseSSRExam(data.id, course._id);

      navigate(`/cursos/${slug}/revisao/exam/${id}`);
    }
  };

  const goToRevisionList = () => {
    navigate(`/cursos/${slug}/revisao?tab=TopicsToReview`);
  };

  const handleSuccess = (data: ICourseSSRExam) => {
    const feedbackScore = data.feedback.score;

    setExercises(organizeExercisesFields(data));
    setFeedbackData({
      correctAnswersPercentage: feedbackScore.percentage,
      correctAnswersTotal: feedbackScore.correctAnswers,
      wrongAnswersTotal:
        feedbackScore.totalQuestions - feedbackScore.correctAnswers,
      timeSpent: 0,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(data);
    }
  }, [isSuccess]);

  return {
    data,
    isError,
    error,
    isSuccess,
    isLoading: isPending,
    exercises,
    wrongAnswersTotal: feedbackData.wrongAnswersTotal,
    correctAnswersTotal: feedbackData.correctAnswersTotal,
    correctAnswersPercentage: feedbackData.correctAnswersPercentage,
    timeSpent: feedbackData.timeSpent,
    remakeExam,
    goToRevisionList,
  };
};
